import React, { useState, useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import styles from "./index.module.css";
import clsx from "clsx";

const ColumnDropdown = ({ name, control, placeholder = "Search columns...", fetchOptions }) => {
  const [options, setOptions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchOptions();
        const data = res?.data || [];
        setOptions(data);
        setFiltered(data);
      } catch (err) {
        console.error("Error fetching options:", err);
      }
    };
    load();
  }, [fetchOptions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (val) => {
    setInputValue(val);
    const search = val.toLowerCase();
    const matches = options.filter((o) =>
      o.label?.toLowerCase().includes(search)
    );
    setFiltered(matches);
    setShowDropdown(true);
  };

  const getSelectedLabels = (selectedIds, allOptions) => {
    if (!Array.isArray(selectedIds)) return [];
    return selectedIds.map((id) => {
      const option = allOptions.find((opt) => opt._id === id);
      return option ? { _id: id, label: option.label } : { _id: id, label: id };
    });
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const selectedIds = Array.isArray(field.value) ? field.value : [];
        const selectedChips = getSelectedLabels(selectedIds, options);
        const hasChips = selectedChips.length > 0;

        const handleSelectOption = (opt) => {
          const currentValue = Array.isArray(field.value) ? field.value : [];
          const isSelected = currentValue.includes(opt._id);

          if (isSelected) {
            field.onChange(currentValue.filter((id) => id !== opt._id));
          } else {
            field.onChange([...currentValue, opt._id]);
          }
          setInputValue("");
        };

        const handleRemoveChip = (id) => {
          const newValue = field.value.filter((x) => x !== id);
          field.onChange(newValue);
        };

        return (
          <div className={styles.dropdownContainer} ref={containerRef}>
            <div
              className={clsx(styles.inputWrapper, {
                [styles.hasChips]: hasChips,
              })}
              onClick={() => {
                if (containerRef.current.querySelector('input[type="text"]')) {
                  containerRef.current.querySelector('input[type="text"]').focus();
                }
                setShowDropdown(true);
              }}
            >
              {hasChips && (
                <div className={styles.chipsInInputContainer}>
                  {selectedChips.map((item) => (
                    <div key={item._id} className={styles.chip}>
                      {item.label}
                      <span
                        className={styles.chipRemove}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveChip(item._id);
                        }}
                      >
                        ×
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <input
                placeholder={hasChips ? "Type to search..." : placeholder}
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={() => setShowDropdown(true)}
                className={clsx(styles.formInput, {
                  [styles.inputWithChips]: hasChips,
                })}
                autoComplete="off"
                style={{ flexGrow: 1 }}
              />
            </div>
            {showDropdown && (
              <div className={styles.dropdown}>
                {filtered.map((opt) => {
                  const isSelected = selectedIds.includes(opt._id);
                  return (
                    <div
                      key={opt._id}
                      className={clsx(styles.dropdownItem, {
                        [styles.selected]: isSelected,
                      })}
                      onClick={() => handleSelectOption(opt)}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        className={styles.checkbox}
                      />
                      {opt.label}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }}
    />
  );
};

export default ColumnDropdown;
